<template>
  <section>
    <div class="row">
      <div class="col-md-12">
        <p>This facility is used by ADAS MAP super administrators to configure the ADAS checklists, notices, and suggestions.</p>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <p>
          Click this
          <a
            href="https://adascopilot.blob.core.windows.net/app/ADAS%20Matrix%20Config%20and%20Inspection%20Screen%20Relationships.pdf"
            target="_blank"
            >link</a
          >
          for a review of the configuration screens and how they relate to the ADAS checklists, notices, and suggestions the user will see.
        </p>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <p>
          The following are Systems and Locations used throughout the ADAS Matrix. Be careful with modifying these as they may impact multiple
          matrices. Proceed with caution.
        </p>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <p><b>NOTE:</b> do not use commas in the System or Location names. This will cause issues with the Import/Export functionality.</p>
      </div>
    </div>
    <div class="row mt-2">
      <div class="col-8">
        <!-- Locations -->
        <span class="custom-title">Locations:</span>
        <input v-model="locationInactive" type="checkbox" class="custom-checkbox ms-3 me-2" />
        <span>show inactive</span>
        <div v-if="isEditLocation" class="custom-setinactive">
          <input v-model="setLocationInactive" type="checkbox" class="custom-checkbox ms-3 me-2" @click="changedInactive('Location')" />
          <span>set inactive</span>
        </div>
        <div class="input-group mb-5 mt-2">
          <input
            v-show="isEditLocation || isAddLocation"
            ref="refEditLocation"
            v-model="modifyLocation"
            type="text"
            class="form-control"
            placeholder="new Location"
            aria-label="new Location"
          />
          <select
            v-show="!isEditLocation && !isAddLocation"
            ref="refSelectLocation"
            v-model="currentLocation"
            class="form-control custom-select"
            @change="selectedLocation"
          >
            <option v-for="item of locations" :key="item.id" :value="item.value">
              {{ item.name }}
            </option>
          </select>
          <button
            v-if="isSelectedLocation && !isEditLocation && !isAddLocation"
            class="btn btn-warning custom-focus"
            type="button"
            title="edit location"
            tabindex="-1"
            @click="editLocation"
          >
            <i class="far fa-edit"></i>
          </button>
          <button
            v-if="isSelectedLocation && !isEditLocation && !isAddLocation"
            class="btn btn-danger custom-focus"
            type="button"
            title="set inactive"
            tabindex="-1"
            @click="removeLocation"
          >
            <i class="far fa-trash-alt"></i>
          </button>
          <button
            v-if="isEditLocation || isAddLocation"
            class="btn btn-warning custom-focus"
            type="button"
            title="edit location"
            tabindex="-1"
            @click="cancel"
          >
            <i class="fas fa-ban"></i>
          </button>
          <button
            v-if="!isEditLocation && !isAddLocation"
            class="btn btn-success custom-focus"
            type="button"
            title="add location"
            tabindex="-1"
            @click="addLocation"
          >
            <i class="fas fa-plus"></i>
          </button>
          <button v-if="isSave && workingOn === 'Location'" class="btn btn-primary custom-focus" type="button" tabindex="-1" @click="saveLocation">
            Save
          </button>
        </div>
        <!-- Systems -->
        <span class="custom-title">Systems:</span>
        <input v-model="systemInactive" type="checkbox" class="custom-checkbox ms-3 me-2" />
        <span>show inactive</span>
        <button class="btn btn-primary btn-sm ms-2" @click="reload('SystemType')">clear</button>
        <span class="custom-system-note">Note: if you wish to edit the system's CCC/Mitchell items, be sure to click the yellow edit button.</span>
        <div v-if="isEditSystem" class="custom-setinactive">
          <input v-model="setSystemInactive" type="checkbox" class="custom-checkbox ms-3 me-2" @click="changedInactive('System')" />
          <span>set inactive</span>
        </div>
        <div class="input-group mb-3 mt-2">
          <input
            v-show="(isEditSystem || isAddSystem) && !viewingInactive"
            ref="refEditSystem"
            v-model="modifySystem"
            type="text"
            class="form-control"
            placeholder="new System"
            aria-label="new System"
          />
          <select
            v-show="!isEditSystem && !isAddSystem"
            ref="refSelectSystem"
            v-model="currentSystem"
            class="form-control custom-select"
            @change="selectedSystem"
          >
            <option v-for="item of systems" :key="item.id" :value="item.value">
              {{ item.name }}
            </option>
          </select>
          <span class="input-group-text">CCC Item</span>
          <select v-model="cccItemType" class="form-control custom-select" title="CCC Item" placeholder="CCC Item">
            <option v-for="item of cccItemTypes" :key="item.id" :value="item.value">
              {{ item.name }}
            </option>
          </select>
          <span class="input-group-text">Mitchell Item</span>
          <select v-model="mitchellItemType" class="form-control custom-select" title="Mitchell Item" placeholder="Mitchell Item">
            <option v-for="item of mitchellItemTypes" :key="item.id" :value="item.value">
              {{ item.name }}
            </option>
          </select>
          <button
            v-if="isSelectedSystem && !isEditSystem && !isAddSystem && !viewingInactive"
            class="btn btn-warning custom-focus"
            type="button"
            title="edit system"
            tabindex="-1"
            @click="editSystem"
          >
            <i class="far fa-edit"></i>
          </button>
          <button
            v-if="isSelectedSystem && !isEditSystem && !isAddSystem && !viewingInactive"
            class="btn btn-danger custom-focus"
            type="button"
            title="set inactive"
            tabindex="-1"
            @click="removeSystem"
          >
            <i class="far fa-trash-alt"></i>
          </button>
          <button
            v-if="isEditSystem || isAddSystem"
            class="btn btn-warning custom-focus"
            type="button"
            title="edit system"
            tabindex="-1"
            @click="cancel"
          >
            <i class="fas fa-ban"></i>
          </button>
          <button
            v-if="!isEditSystem && !isAddSystem"
            class="btn btn-success custom-focus"
            type="button"
            title="add system"
            tabindex="-1"
            @click="addSystem"
          >
            <i class="fas fa-plus"></i>
          </button>
          <button v-if="isSave && workingOn === 'System'" class="btn btn-primary custom-focus" type="button" tabindex="-1" @click="saveSystem">
            Save
          </button>
        </div>
        <div class="row">
          <!-- System Calibrations -->
          <div v-if="isSelectedSystem" class="input-group mb-3">
            <span class="input-group-text">Add Calibration</span>
            <select
              v-model="calibrationType"
              class="form-control custom-select"
              title="Calibration Service"
              placeholder="Calibration Service"
              @change="changedCalibration"
            >
              <option v-for="item of lookups['CalibrationType']" :key="item.id" :value="item.value">
                {{ item.name }}
              </option>
            </select>
            <button v-if="isCalibrationChanged" class="btn btn-primary custom-focus" type="button" tabindex="-1" @click="saveCalibration">
              <i class="fas fa-plus"></i>
            </button>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <ul class="custom-list">
              <li v-for="item in calibrationsOfSystem" :key="item.id">
                <span>{{ item.name }}</span>
                <div class="custom-trash" @click="removeCalibration(item.id)"><i class="far fa-trash-alt"></i></div>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="col-6">
        <!-- Insurance Pricing -->
        <div class="row">
          <div class="col">
            <span class="custom-title">Insurance Pricing by Calibration:</span>
            <select
              v-model="calibrationTypePricing"
              class="form-control custom-select custom-calibration mt-2"
              title="Calibration Service"
              placeholder="Calibration Service"
            >
              <option v-for="item of lookups['CalibrationType']" :key="item.id" :value="item.value">
                {{ item.name }}
              </option>
            </select>
            <div v-if="calibrationTypeForPricing !== null" class="input-group mb-3 mt-2">
              <select
                ref="refSelectInsurance"
                v-model="currentInsurance"
                class="form-control custom-select custom-price-insurance"
                @change="selectedInsurance"
              >
                <option v-for="item of insurance" :key="item.id" :value="item.value">
                  {{ item.name }}
                </option>
              </select>
              <input
                ref="refEditPrice"
                v-model="modifyPrice"
                type="number"
                min="0"
                max="9999"
                class="form-control custom-price"
                placeholder="price"
                aria-label="price"
              />
              <button class="btn btn-warning custom-focus" type="button" title="cancel price" tabindex="-1" @click="cancelPrice">
                <i class="fas fa-ban"></i>
              </button>
              <button
                class="btn btn-success custom-focus"
                type="button"
                title="save price"
                tabindex="-1"
                :disabled="!verifiedPrice()"
                @click="savePrice"
              >
                <i class="fas fa-plus"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col custom-price-list">
            <ul class="custom-list">
              <li v-for="item in calibrationPricing" :key="item.calibrationPrice">
                <div class="row">
                  <div class="col-9">
                    <span>{{ item.insuranceName }}</span>
                  </div>
                  <div class="col-2">
                    <span>${{ formatPrice(item.price) }}</span>
                  </div>
                  <div class="col-1">
                    <a href="#" class="custom-remove" @click="removePrice(item)">
                      <i class="fas fa-times text-danger"></i>
                    </a>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <confirm
      v-model="actionDelete"
      :confirm-title="confirmTitle"
      :confirm-action="confirmAction"
      :confirm-question="confirmQuestion"
      @closing="closeModal"
      @proceed="performDelete"
    />
  </section>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { authStore } from '@/stores/auth'
import { adasmatrixStore } from '@/stores/adasmatrix'
import _ from 'lodash'
import Confirm from '@/components/utilities/elements/ConfirmDialog.vue'
export default {
  name: 'AdminConfigGeneral',
  components: {
    Confirm
  },
  data() {
    return {
      isEditSystem: false,
      isAddSystem: false,
      modifySystem: null,
      currentSystem: null,
      chkInactiveSystem: false,
      isSelectedSystem: false,
      isEditLocation: false,
      isAddLocation: false,
      modifyLocation: null,
      isCalibrationChanged: false,
      systemCalibration: null,
      currentLocation: null,
      chkInactiveLocation: false,
      isSelectedLocation: false,
      confirmTitle: 'Remove?',
      confirmAction: 'Remove',
      confirmQuestion: 'Are you sure you wish to Remove (set inactive) this item?',
      actionDelete: null,
      isSave: false,
      inactiveChanged: false,
      setSystemInactive: false,
      setLocationInactive: false,
      currentInsurance: null,
      modifyPrice: null,
      actionPriceItem: null,
      calibrationTypeForPricing: null,
      calibrationType: null,
      cccItemType: null,
      mitchellItemType: null
    }
  },
  mounted() {
    this.isCalibrationChanged = false
    this.inactiveChanged = false
    this.setSystemInactive = false
    this.setLocationInactive = false
    this.cancel()
    this.resetPrices()
  },
  computed: {
    ...mapState(authStore, ['profile']),
    ...mapState(adasmatrixStore, ['loading', 'lookups', 'calibrationPricing', 'systemCalibrations']),
    systems() {
      let cnt = 1
      let _data = []
      if (this.lookups) {
        if (this.lookups['SystemType']) {
          let types = JSON.parse(JSON.stringify(this.lookups['SystemType']))
          _data = _.sortBy(types, ['name'])
          _data.forEach((element) => {
            element.id = cnt
            cnt++
          })
        }
      }
      return _data
    },
    locations() {
      let cnt = 1
      let _data = []
      if (this.lookups) {
        if (this.lookups['LocationType']) {
          let types = JSON.parse(JSON.stringify(this.lookups['LocationType']))
          _data = _.sortBy(types, ['name'])
          _data.forEach((element) => {
            element.id = cnt
            cnt++
          })
        }
      }
      return _data
    },
    insurance() {
      let cnt = 1
      let _data = []
      if (this.lookups) {
        if (this.lookups['Insurance']) {
          let types = JSON.parse(JSON.stringify(this.lookups['Insurance']))
          _data = _.sortBy(types, ['name'])
          _data.forEach((element) => {
            element.id = cnt
            cnt++
          })
        }
      }
      return _data
    },
    cccItemTypes() {
      let cnt = 1
      let _data = []
      if (this.lookups) {
        if (this.lookups['CCCItemType']) {
          let types = JSON.parse(JSON.stringify(this.lookups['CCCItemType']))
          _data = _.sortBy(types, ['name'])
          _data.forEach((element) => {
            element.id = cnt
            cnt++
          })
          _data.unshift({
            id: 0,
            name: '',
            value: null
          })
        }
      }
      return _data
    },
    mitchellItemTypes() {
      let cnt = 1
      let _data = []
      if (this.lookups) {
        if (this.lookups['MitchellItemType']) {
          let types = JSON.parse(JSON.stringify(this.lookups['MitchellItemType']))
          _data = _.sortBy(types, ['name'])
          _data.forEach((element) => {
            element.id = cnt
            cnt++
          })
          _data.unshift({
            id: 0,
            name: '',
            value: null
          })
        }
      }
      return _data
    },
    calibrationsOfSystem() {
      let _items = []
      console.log('Calibrations of System', this.lookups['CalibrationType'])
      if (this.lookups['CalibrationType']) {
        let ids = this.systemCalibrations.map((x) => parseInt(x.id))
        _items = this.lookups['CalibrationType'].filter((x) => ids.includes(x.id))
      }
      return _items
    },
    calibrationTypePricing: {
      get() {
        let _item = null
        if (this.calibrationTypeForPricing) {
          let temp = this.lookups['CalibrationType'].find((x) => parseInt(x.id) === parseInt(this.calibrationTypeForPricing))
          if (temp) {
            _item = temp.id
          }
        }
        return _item
      },
      set(value) {
        this.changedCalibrationPricing(value)
      }
    },
    workingOn() {
      let _on = null
      if (this.isEditSystem || this.isAddSystem) {
        _on = 'System'
      } else if (this.isEditLocation || this.isAddLocation) {
        _on = 'Location'
      }
      return _on
    },
    systemInactive: {
      get() {
        return this.chkInactiveSystem
      },
      set(value) {
        this.chkInactiveSystem = value
        this.reload('SystemType')
      }
    },
    locationInactive: {
      get() {
        return this.chkInactiveLocation
      },
      set(value) {
        this.chkInactiveLocation = value
        this.reload('LocationType')
      }
    },
    viewingInactive() {
      let _is = false
      if (this.chkInactiveSystem || this.chkInactiveLocation) {
        _is = true
      }
      return _is
    }
  },
  methods: {
    ...mapActions(adasmatrixStore, [
      'getLookups',
      'upsertLookup',
      'deleteCalibration',
      'resetPrices',
      'resetCalibrations',
      'getCalibrationPricing',
      'saveCalibrationPricing',
      'getSystemCalibrations'
    ]),
    closeModal() {
      this.actionDelete = null
    },
    async reload(what) {
      await this.getLookups({
        keys: [what],
        firm: 1,
        refresh: true,
        showInactive: what === 'SystemType' ? this.chkInactiveSystem : this.chkInactiveLocation
      })
      this.currentSystem = null
      this.currentLocation = null
      this.resetCalibrations()
      this.resetPrices()
      this.cancel()
    },
    async selectedSystem() {
      this.isSelectedSystem = true
      this.systemCalibration = null
      this.isCalibrationChanged = false
      this.inactiveChanged = false
      this.setSystemInactive = false
      this.setLocationInactive = false
      this.cccItemType = null
      this.mitchellItemType = null
      this.calibrationType = null
      if (this.currentSystem) {
        let found = this.systems.find((x) => parseInt(x.value) === parseInt(this.currentSystem))
        this.systemCalibration = JSON.parse(JSON.stringify(found))
        this.cccItemType = this.systemCalibration.type2 ? parseInt(this.systemCalibration.type2) : null
        this.mitchellItemType = this.systemCalibration.type3 ? parseInt(this.systemCalibration.type3) : null
        await this.getSystemCalibrations(this.systemCalibration.value)
      }
      let cccItemType = this.lookups['CCCItemType'].find((x) => parseInt(x.id) === parseInt(this.systemCalibration.type2))
      console.log('Selected System', {
        systemCalibration: this.systemCalibration,
        cccItemType: cccItemType,
        mitchellItemType: this.mitchellItemType
      })
    },
    selectedLocation() {
      this.isSelectedLocation = true
    },
    cancel() {
      this.modifySystem = null
      this.isEditLocation = false
      this.isAddLocation = false
      this.modifyLocation = null
      this.inactiveChanged = false
      this.setSystemInactive = false
      this.setLocationInactive = false
      this.isSave = false
      setTimeout(() => {
        if (this.workingOn === 'System') {
          this.$refs.refSelectSystem.focus()
        } else {
          this.$refs.refSelectLocation.focus()
        }
      }, 250)
      this.isEditSystem = false
      this.isAddSystem = false
      this.modifyPrice = null
      this.currentInsurance = null
    },
    cancelPrice() {
      this.modifyPrice = null
      this.currentInsurance = null
    },
    async changedCalibrationPricing(value) {
      this.resetPrices()
      this.calibrationTypeForPricing = parseInt(value)
      await this.getCalibrationPricing(value)
    },
    async changedCalibration() {
      this.isCalibrationChanged = true
      console.log('Selected Calibration', {
        systemCalibration: this.systemCalibration,
        calibrationType: this.calibrationType
      })
    },
    changedInactive(what) {
      if (what === 'System') {
        this.inactiveChanged = true
      } else {
        this.inactiveChanged = true
      }
    },
    async addSystem() {
      if (this.chkInactiveSystem) {
        let found = this.systems.find((x) => parseInt(x.value) === parseInt(this.currentSystem))
        if (found) {
          // save
          let data = {
            firm: 1,
            id: parseInt(found.value),
            key: 'SystemType',
            name: found.name.trim(),
            type1: null,
            type2: null,
            isInactive: false
          }
          await this.upsertLookup(data)
          await this.reload('SystemType')
        }
      } else {
        this.cccItemType = null
        this.mitchellItemType = null
        this.isEditSystem = false
        this.isAddSystem = true
        this.modifySystem = null
        this.isSelectedSystem = false
        this.isSave = true
        setTimeout(() => {
          this.$refs.refEditSystem.focus()
        }, 250)
      }
    },
    editSystem() {
      this.isEditSystem = false
      this.isAddSystem = false
      this.isSave = false
      this.modifySystem = null
      let found = this.systems.find((x) => parseInt(x.value) === parseInt(this.currentSystem))
      if (found) {
        this.modifySystem = found.name.trim()
        this.isEditSystem = true
        this.isSave = true
        setTimeout(() => {
          this.$refs.refEditSystem.focus()
        }, 250)
      }
    },
    async saveSystem() {
      let valid = true
      let found = null
      let reason = 'Please supply a system name'
      if (this.modifySystem === null) {
        valid = false
      } else if (this.modifySystem.trim() === '') {
        valid = false
      } else {
        found = this.systems.find((x) => x.name != null && x.name.trim() === this.modifySystem.trim())
        if (found && !this.isEditSystem) {
          valid = false
          reason = 'System already exists'
        }
      }
      if (valid) {
        let data = {
          firm: 1,
          id: null,
          key: 'SystemType',
          name: this.modifySystem.trim(),
          type1: null,
          type2: this.cccItemType ? parseInt(this.cccItemType) : null,
          type3: this.mitchellItemType ? parseInt(this.mitchellItemType) : null,
          isInactive: false
        }
        if (this.isEditSystem) {
          let found = this.systems.find((x) => parseInt(x.value) === parseInt(this.currentSystem))
          if (found) {
            // save
            data = {
              firm: 1,
              id: parseInt(found.value),
              key: 'SystemType',
              name: this.modifySystem.trim(),
              type1: found.type1,
              type2: this.cccItemType ? parseInt(this.cccItemType) : null,
              type3: this.mitchellItemType ? parseInt(this.mitchellItemType) : null,
              isInactive: this.setSystemInactive
            }
          }
        }
        await this.upsertLookup(data)
        this.isEditSystem = false
        this.isAddSystem = false
        this.isSave = false
        this.modifySystem = null
        this.cccItemType = null
        this.mitchellItemType = null
        await this.reload('SystemType')
      } else {
        alert(reason)
      }
    },
    removeSystem() {
      this.confirmTitle = 'Remove System?'
      this.confirmQuestion = 'Are you sure you wish to Remove (set inactive) this System?'
      this.actionDelete = {
        name: 'System'
      }
    },
    removeCalibration(id) {
      this.confirmTitle = 'Remove Calibration?'
      this.confirmQuestion = 'Are you sure you wish to Remove this Calibration?'
      this.actionDelete = {
        name: 'Calibration',
        calibrationType: id
      }
    },
    addLocation() {
      this.isEditLocation = false
      this.isAddLocation = true
      this.modifyLocation = null
      this.isSave = true
      setTimeout(() => {
        this.$refs.refEditLocation.focus()
      }, 250)
    },
    editLocation() {
      this.isEditLocation = false
      this.isAddLocation = false
      this.isSave = false
      this.modifyLocation = null
      let found = this.locations.find((x) => parseInt(x.value) === parseInt(this.currentLocation))
      if (found) {
        this.modifyLocation = found.name.trim()
        this.isEditLocation = true
        this.isSave = true
        setTimeout(() => {
          this.$refs.refEditLocation.focus()
        }, 250)
      }
    },
    async saveLocation() {
      let valid = true
      let found = null
      let reason = 'Please supply a location name'
      if (this.modifyLocation === null) {
        valid = false
      } else if (this.modifyLocation.trim() === '') {
        valid = false
      } else {
        found = this.locations.find((x) => x.name != null && x.name.trim() === this.modifyLocation.trim())
        if (found && !this.isEditLocation) {
          valid = false
          reason = 'Location already exists'
        }
      }
      if (valid) {
        let data = {
          firm: 1,
          id: null,
          key: 'LocationType',
          name: this.modifyLocation.trim(),
          isInactive: false
        }
        if (this.isEditLocation) {
          found = this.locations.find((x) => parseInt(x.value) === parseInt(this.currentLocation))
          if (found) {
            // save
            data = {
              firm: 1,
              id: parseInt(found.value),
              key: 'LocationType',
              name: this.modifyLocation.trim(),
              isInactive: this.setLocationInactive
            }
          }
        }
        await this.upsertLookup(data)
        this.isEditLocation = false
        this.isAddLocation = false
        this.isSave = false
        this.modifyLocation = null
        await this.reload('LocationType')
      } else {
        alert(reason)
      }
    },
    removeLocation() {
      this.confirmTitle = 'Remove Location?'
      this.confirmQuestion = 'Are you sure you wish to Remove (set inactive) this Location?'
      this.actionDelete = {
        name: 'Location'
      }
    },
    selectedInsurance() {
      // console.log('Selected Insurance', this.currentInsurance)
    },
    formatPrice(value) {
      return value === null ? 0.0 : value.toFixed(2)
    },
    removePrice(item) {
      this.confirmTitle = 'Remove Price?'
      this.confirmQuestion = 'Are you sure you wish to Remove (set inactive) this Price?'
      this.actionDelete = {
        name: 'Price',
        calibrationPrice: item.calibrationPrice,
        price: item.price,
        insurance: item.insurance,
        calibrationType: item.calibrationType,
        systemType: item.systemType,
        isLocked: true,
        isInactive: true,
        user: this.profile.user
      }
    },
    verifiedPrice() {
      let valid = true
      if (typeof this.modifyPrice === 'string' || this.modifyPrice instanceof String) {
        if (this.modifyPrice.trim() === '') {
          valid = false
        }
      }
      if (this.modifyPrice === null) {
        valid = false
      } else if (parseInt(this.modifyPrice) < 0) {
        valid = false
      } else if (parseInt(this.modifyPrice) > 9999) {
        valid = false
      }
      if (this.currentInsurance === null) {
        valid = false
      }
      return valid
    },
    async savePrice() {
      let found = this.calibrationPricing.find(
        (x) => parseInt(x.insurance) === parseInt(this.currentInsurance) && parseInt(x.calibrationType) === parseInt(this.calibrationTypeForPricing)
      )
      if (!found) {
        let data = {
          price: this.modifyPrice,
          insurance: this.currentInsurance,
          calibrationType: this.calibrationTypeForPricing,
          systemType: this.currentSystem,
          isLocked: true,
          user: this.profile.user
        }
        console.log('Save Price', {
          pricing: this.calibrationPricing,
          data: data
        })
        await this.saveCalibrationPricing(data)
        this.cancelPrice()
        this.changedCalibrationPricing(this.calibrationTypeForPricing)
      } else {
        alert('Pricing already exists')
      }
    },
    async performDelete() {
      let found = null
      let data = null
      if (this.actionDelete.name === 'Price') {
        console.log('Remove Price', this.actionDelete)
        await this.saveCalibrationPricing(this.actionDelete)
        this.actionDelete = null
        this.cancelPrice()
        this.changedCalibrationPricing(this.calibrationTypeForPricing)
      } else if (this.actionDelete.name === 'Calibration') {
        console.log('Removing Calibration', this.currentSystem)
        await this.deleteCalibration({
          systemType: this.currentSystem,
          calibrationType: this.actionDelete.calibrationType
        })
        this.selectedSystem()
      } else if (this.actionDelete.name === 'System') {
        console.log('Removing System', this.currentSystem)
        found = this.systems.find((x) => parseInt(x.value) === parseInt(this.currentSystem))
        if (found) {
          // remove
          data = {
            firm: 1,
            id: parseInt(found.value),
            key: 'SystemType',
            name: found.name.trim(),
            type1: null,
            type2: null,
            isInactive: true
          }
          await this.upsertLookup(data)
          await this.reload('SystemType')
          this.cccItemType = null
          this.mitchellItemType = null
          this.isSelectedSystem = false
        }
      } else {
        console.log('Removing Location', this.currentLocation)
        found = this.locations.find((x) => parseInt(x.value) === parseInt(this.currentLocation))
        if (found) {
          // remove
          data = {
            firm: 1,
            id: parseInt(found.value),
            key: 'LocationType',
            name: found.name.trim(),
            isInactive: true
          }
          await this.upsertLookup(data)
          await this.reload('LocationType')
        }
      }
      this.actionDelete = null
    },
    async saveCalibration() {
      console.log('Save Calibration', this.systemCalibration)
      this.systemCalibration.type1 = this.calibrationType ? parseInt(this.calibrationType) : null
      this.systemCalibration.type2 = this.cccItemType ? parseInt(this.cccItemType) : null
      this.systemCalibration.type3 = this.mitchellItemType ? parseInt(this.mitchellItemType) : null
      let data = {
        firm: 1,
        id: parseInt(this.systemCalibration.value),
        key: 'SystemType',
        name: this.systemCalibration.name.trim(),
        isInactive: false,
        type1: this.systemCalibration.type1,
        type2: this.systemCalibration.type2,
        type3: this.systemCalibration.type3
      }
      await this.upsertLookup(data)
      this.isCalibrationChanged = false
      await this.reload('SystemType')
      this.currentSystem = parseInt(this.systemCalibration.value)
      this.selectedSystem()
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-setinactive {
  display: inline-block;
  float: right;
  margin-right: 25px;
}
.custom-focus:focus {
  border-color: inherit;
  -webkit-box-shadow: none;
  box-shadow: none;
  outline: none;
  border: transparent;
}
.custom-title {
  font-weight: bold;
}
.custom-calibration {
  width: 50%;
}
.custom-price-insurance {
  width: auto;
}
.custom-price {
  width: 100px !important;
}
.custom-price-list {
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 350px;
}
.custom-list {
  list-style-type: none;
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 400px;
  li {
    padding: 5px;
  }
}
.custom-trash {
  float: right;
  cursor: pointer;
  color: rgb(122, 0, 0);
}
.custom-system-note {
  font-size: 0.8rem;
  margin-left: 10px;
}
</style>
